/**
 * Modal dialog and confirmation prompt.
 *
 * Deliberately conservative for a clinical console: clicking the backdrop does
 * nothing, so a stray click cannot discard a half-filled form. Escape and the
 * explicit Cancel button are the ways out, and destructive confirmations name
 * the thing being destroyed.
 */

import { useEffect, type ReactNode } from 'react'

export function Modal({
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="subtitle">{subtitle}</p>}
          </div>
          <button className="btn btn-small" type="button" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  title,
  confirmLabel,
  destructive,
  busy,
  error,
  onConfirm,
  onCancel,
  children,
}: {
  title: string
  confirmLabel: string
  destructive?: boolean
  busy?: boolean
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
  children: ReactNode
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className={destructive ? 'btn btn-danger' : 'btn btn-primary'}
            type="button"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}
      {children}
    </Modal>
  )
}
